{
  "version": "0.1.38",
  "package": "@classic-homes/theme-styles",
  "description": "Base CSS styles, accessibility features, and utility classes for the Classic Theme design system",
  "integration": {
    "import": "@import '@classic-homes/theme-styles';",
    "placement": "Before Tailwind directives in app.css",
    "example": "@import '@classic-homes/theme-styles';\n@tailwind base;\n@tailwind components;\n@tailwind utilities;"
  },
  "layers": {
    "order": [
      "classic-theme.tokens",
      "classic-theme.base",
      "classic-theme.components",
      "classic-theme.utilities"
    ],
    "description": "CSS layers are used for proper cascade control, from lowest to highest specificity"
  },
  "modules": [
    {
      "name": "base",
      "path": "@classic-homes/theme-styles/base",
      "description": "Foundational typography and base element styles following Classic Homes brand guidelines",
      "features": [
        {
          "name": "Brand Fonts",
          "description": "Imports Google Fonts: Figtree (sans-serif) and Source Serif 4 (serif)",
          "cssVariables": [
            "--font-sans",
            "--font-serif"
          ]
        },
        {
          "name": "HTML/Body Setup",
          "description": "Base font family, size, line height, font smoothing, and background/foreground colors"
        },
        {
          "name": "Heading Styles",
          "description": "h1-h6 with serif font (Source Serif 4), proper sizing, and brand-consistent styling",
          "sizes": {
            "h1": "2.25rem (36px)",
            "h2": "1.875rem (30px)",
            "h3": "1.5rem (24px)",
            "h4": "1.25rem (20px)",
            "h5": "1.125rem (18px)",
            "h6": "1rem (16px)"
          }
        },
        {
          "name": "Paragraph Styles",
          "description": "Paragraph elements with proper margin spacing (0 top, 1rem bottom)"
        },
        {
          "name": "Link Styles",
          "description": "Primary color links with hover underline transition"
        },
        {
          "name": "Strong/Bold Text",
          "description": "Font weight 600 for strong and b elements"
        },
        {
          "name": "Code Blocks",
          "description": "Monospace font styling for code, kbd, pre, and samp elements with muted background"
        },
        {
          "name": "Lists",
          "description": "Ordered and unordered lists with proper margin and padding (1.5rem left padding)"
        },
        {
          "name": "Horizontal Rule",
          "description": "Styled hr with 1px border using border color, 1.5rem vertical margin"
        },
        {
          "name": "Images",
          "description": "Responsive images with max-width 100%, auto height, block display"
        },
        {
          "name": "Tables",
          "description": "Collapsed borders, full width, proper padding and borders for th/td"
        },
        {
          "name": "Form Element Resets",
          "description": "Button, input, select, textarea inherit font-family, font-size, line-height"
        },
        {
          "name": "Blockquotes",
          "description": "Left border accent with primary color, italic styling, muted foreground"
        },
        {
          "name": "Selection",
          "description": "Custom text selection with primary color background at 20% opacity"
        }
      ]
    },
    {
      "name": "accessibility",
      "path": "@classic-homes/theme-styles/accessibility",
      "description": "WCAG 2.1 AA compliant styles for focus states, touch targets, and screen reader utilities",
      "features": [
        {
          "name": "Focus Visible (WCAG 2.4.7)",
          "description": "Keyboard-only focus indicators with brand ring color",
          "selector": ":focus-visible",
          "style": "2px solid ring color with 2px offset"
        },
        {
          "name": "Skip Link (WCAG 2.4.1)",
          "class": ".skip-link",
          "description": "Hidden link that appears on focus for keyboard users to bypass navigation",
          "usage": "<a href=\"#main-content\" class=\"skip-link\">Skip to content</a>"
        },
        {
          "name": "Touch Targets (WCAG 2.5.5)",
          "classes": [
            ".touch-target",
            ".touch-target-expand"
          ],
          "description": "Minimum 44x44px touch targets for mobile accessibility",
          "usage": ".touch-target for direct sizing, .touch-target-expand for pseudo-element expansion"
        },
        {
          "name": "Reduced Motion (WCAG 2.3.3)",
          "description": "Respects prefers-reduced-motion media query by disabling animations",
          "mediaQuery": "@media (prefers-reduced-motion: reduce)"
        },
        {
          "name": "Screen Reader Only",
          "classes": [
            ".sr-only",
            ".sr-only-focusable"
          ],
          "description": "Visually hidden but accessible to screen readers",
          "usage": ".sr-only for always hidden, .sr-only-focusable for hidden until focused"
        },
        {
          "name": "High Contrast Mode",
          "description": "Support for Windows High Contrast Mode",
          "mediaQuery": "@media (forced-colors: active)"
        },
        {
          "name": "Focus Ring Utility",
          "class": ".focus-ring",
          "description": "Enhanced focus indicator with box-shadow ring effect"
        },
        {
          "name": "Not Focusable",
          "class": ".not-focusable",
          "description": "Removes focus indicators for decorative elements"
        }
      ]
    },
    {
      "name": "utilities",
      "path": "@classic-homes/theme-styles/utilities",
      "description": "Common utility classes for scrollbars, transitions, text, layout, and animations",
      "classes": [
        {
          "category": "Scrollbar",
          "items": [
            {
              "class": ".scrollbar-hide",
              "description": "Hides scrollbar while maintaining scroll functionality",
              "browsers": "IE, Edge, Firefox, Chrome, Safari, Opera"
            },
            {
              "class": ".scrollbar-thin",
              "description": "Thin 6px scrollbar with subtle styling",
              "style": "Muted foreground color at 30% opacity"
            }
          ]
        },
        {
          "category": "Transitions",
          "items": [
            {
              "class": ".transition-colors",
              "description": "Transitions color, background-color, border-color, text-decoration-color, fill, stroke",
              "duration": "150ms"
            },
            {
              "class": ".transition-opacity",
              "description": "Transitions opacity",
              "duration": "150ms"
            },
            {
              "class": ".transition-transform",
              "description": "Transitions transform",
              "duration": "150ms"
            },
            {
              "class": ".transition-all",
              "description": "Transitions all properties",
              "duration": "150ms"
            },
            {
              "class": ".transition-menu-item",
              "description": "Optimized transition for sidebar/nav menu items",
              "properties": "background-color, color (150ms), transform (100ms)"
            }
          ]
        },
        {
          "category": "Text",
          "items": [
            {
              "class": ".truncate",
              "description": "Truncates text with ellipsis on single line"
            },
            {
              "class": ".line-clamp-1",
              "description": "Clamps text to 1 line with ellipsis"
            },
            {
              "class": ".line-clamp-2",
              "description": "Clamps text to 2 lines with ellipsis"
            },
            {
              "class": ".line-clamp-3",
              "description": "Clamps text to 3 lines with ellipsis"
            }
          ]
        },
        {
          "category": "Layout",
          "items": [
            {
              "class": ".container-default",
              "description": "Responsive container with max-width and horizontal padding",
              "breakpoints": {
                "default": "100% width, 1rem padding",
                "sm (640px)": "max-width 640px",
                "md (768px)": "max-width 768px",
                "lg (1024px)": "max-width 1024px",
                "xl (1280px)": "max-width 1280px"
              }
            }
          ]
        },
        {
          "category": "Interactive States",
          "items": [
            {
              "class": ".disabled",
              "description": "Disabled state with pointer-events: none, 50% opacity, not-allowed cursor"
            },
            {
              "class": ".hover-lift",
              "description": "Lifts element on hover with translateY(-2px) and subtle shadow"
            }
          ]
        },
        {
          "category": "Animations",
          "items": [
            {
              "class": ".animate-spin",
              "description": "360° rotation, 1s linear infinite"
            },
            {
              "class": ".animate-spin-slow",
              "description": "360° rotation, 2s linear infinite"
            },
            {
              "class": ".animate-pulse",
              "description": "Opacity pulse from 100% to 50%, 2s infinite"
            },
            {
              "class": ".animate-fade-in",
              "description": "Fade in from 0 to 1 opacity, 200ms ease-out"
            },
            {
              "class": ".animate-slide-up",
              "description": "Slide up from 10px with fade, 200ms ease-out"
            },
            {
              "class": ".animate-slide-down",
              "description": "Slide down from -10px with fade, 200ms ease-out"
            }
          ]
        },
        {
          "category": "Print",
          "items": [
            {
              "class": ".print-hidden",
              "description": "Hidden when printing"
            },
            {
              "class": ".print-visible",
              "description": "Visible only when printing"
            }
          ]
        }
      ]
    }
  ],
  "peerDependencies": {
    "@classic-homes/theme-tokens": "Required for CSS custom properties (--foreground, --background, --primary, etc.)"
  }
}